import React, { useState } from 'react';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import PasswordRequirements from '../components/PasswordRequirements';
import { resetPassword } from '../services/auth';
import { getUrlParamValue } from '../utils/data';

import { useNavigate } from 'react-router-dom';

import { Modal } from 'react-bootstrap';
import i18n from '../utils/i18n';
import Button from '../components/Button';

type FormValues = {
  password: string;
  confirmPassword: string;
};

const validationSchema = Yup.object().shape({
  password: Yup.string().required('Obrigatório').trim(),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], `A senha deve corresponder`)
    .required('Obrigatório')
    .trim(),
});

const ResetPassword = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [passwordRequirementsMet, setPasswordRequirementsMet] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const onSubmit = async (values: FormValues) => {
    try {
      setLoading(true);

      const token = getUrlParamValue('token');

      if (token) {
        await resetPassword(values.password, token);
        setShowConfirmationModal(true);
      }

    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    isValid,
    errors,
    touched,
    values,
  } = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema,
    onSubmit,
    validateOnBlur: false,
    initialErrors: {
      password: 'Obrigatório',
      confirmPassword: 'Obrigatório',
    },
  });

  return (
    <>
      <div className="auth-container">
        <div className="container-fluid h-100">
          <div className="row h-100 align-items-center justify-content-center">
            <div className="col-12 col-md-8 col-lg-6 col-xl-5">
              <div className="auth-card">
                <div className="auth-logo-container">
                  <img
                    src="assets/images/logo-only.png"
                    className="auth-logo"
                    alt="Silk Secret"
                  />
                  <h1 className="auth-brand-name">silk secret</h1>
                </div>

                <h2 className="auth-title">{i18n.t<string>('ResetPassword.title')}</h2>

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="input-group">
                    <i className="ti-lock input-icon"></i>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      placeholder={i18n.t<string>('ResetPassword.form.newPassword')}
                      id="password"
                      onChange={(e) => {
                        const text = e.target.value;
                        setFieldValue('password', text.trim());
                      }}
                      onBlur={handleBlur}
                      disabled={loading}
                      value={values.password}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i
                        className={`feather-${
                          showPassword ? 'eye' : 'eye-off'
                        }`}
                      ></i>
                    </button>
                    {touched.password && errors.password && (
                      <div className="input-error">
                        <i className="feather-alert-circle"></i>
                        {errors.password}
                      </div>
                    )}
                  </div>

                  <PasswordRequirements
                    password={values.password}
                    onMeetAllRequirements={() => {
                      setPasswordRequirementsMet(true);
                    }}
                    onRequirementsNotMet={() => {
                      setPasswordRequirementsMet(false);
                    }}
                  />

                  <div className="input-group">
                    <i className="ti-lock input-icon"></i>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="form-input"
                      placeholder={i18n.t<string>('ResetPassword.form.confirmNewPassword')}
                      id="confirmPassword"
                      onChange={(e) => {
                        const text = e.target.value;
                        setFieldValue('confirmPassword', text.trim());
                      }}
                      onBlur={handleBlur}
                      disabled={loading}
                      value={values.confirmPassword}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i
                        className={`feather-${
                          showConfirmPassword ? 'eye' : 'eye-off'
                        }`}
                      ></i>
                    </button>
                    {touched.confirmPassword && errors.confirmPassword && (
                      <div className="input-error">
                        <i className="feather-alert-circle"></i>
                        {errors.confirmPassword}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={!isValid || !passwordRequirementsMet || loading}
                    className="btn-auth"
                  >
                    {loading ? (
                      <div className="auth-loading">
                        <div className="spinner"></div>
                        <span>{i18n.t<string>('ResetPassword.resetting')}</span>
                      </div>
                    ) : (
                      i18n.t<string>('ResetPassword.button')
                    )}
                  </button>
                </form>

                <div className="signup-link">
                  <a href="/login">
                    Login
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Modal
          show={showConfirmationModal}
          onHide={() => setShowConfirmationModal(false)}
        >
          <Modal.Header
            closeButton
            onClick={() => setShowConfirmationModal(false)}
            className="bg-white"
            style={{
              border: 0,
              borderRadius: 0,
              padding: 20,
              borderTopLeftRadius: 10,
              WebkitBorderTopRightRadius: 10,
            }}
          />
          <Modal.Body
            className="bg-white"
            style={{
              borderBottomLeftRadius: 10,
              borderBottomRightRadius: 10,
            }}
          >
            <p className="font-xs text-black">
              {i18n.t<string>('ResetPassword.confirmationMessage')}
            </p>
            <div className="form-group mb-1">
              <Button
                onClick={() => {
                  setShowConfirmationModal(false);
                  navigate('/login');
                }}
              >
                {i18n.t<string>('Global.ok')}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default ResetPassword;